import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  fetchMe,
  login as loginRequest,
  logout as logoutRequest,
  resendOtp as resendOtpRequest,
  verifyOtp as verifyOtpRequest,
} from '@/api/auth';
import { setAuthToken, setUnauthorizedHandler } from '@/api/client';
import { accountChoicesFrom } from '@/api/errors';
import {
  isOtpRequired,
  type LoginInput,
  type LoginResponse,
  type PortalUser,
  type SignInResult,
  type VerifyOtpInput,
} from '@/types/auth';

import { AuthContext, type AuthContextValue, type AuthStatus } from './authContext';
import { authKeys } from './authKeys';
import { authStorage } from './authStorage';
import {
  announceSignOut,
  canShareSession,
  listenToOtherTabs,
  requestTokenFromOtherTabs,
} from './authSync';

/**
 * Read the stored token and install it on the API client synchronously, before
 * the first render. Doing this in an effect would let the initial /auth/me
 * request go out without an Authorization header and 401 immediately.
 */
function primeTokenFromStorage(): string | null {
  const token = authStorage.get();
  setAuthToken(token);
  return token;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(primeTokenFromStorage);

  /*
   * A tab that opens without a token of its own — Settings opened in a new tab
   * from the right-click menu, say — asks the other open tabs for theirs before
   * concluding nobody is signed in (see authSync.ts). Decided once, at start-up:
   * a tab that later signs out must not go looking for a session to rejoin.
   */
  const [askingOtherTabs, setAskingOtherTabs] = useState(() => token === null && canShareSession());

  const clearSession = useCallback(() => {
    authStorage.clear();
    setAuthToken(null);
    setToken(null);
    // Essential: without it, the next person to sign in on this tab would
    // briefly see the previous user's cached documents.
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    if (!askingOtherTabs) return;

    const controller = new AbortController();
    void requestTokenFromOtherTabs({ signal: controller.signal }).then((shared) => {
      if (controller.signal.aborted) return;
      if (shared) {
        authStorage.set(shared);
        setAuthToken(shared);
        setToken(shared);
      }
      setAskingOtherTabs(false);
    });

    return () => controller.abort();
  }, [askingOtherTabs]);

  // Answer tabs opened after this one, and follow a sign-out made in another.
  useEffect(
    () =>
      listenToOtherTabs({
        getToken: () => authStorage.get(),
        onSignedOut: (signedOut) => {
          if (signedOut === authStorage.get()) clearSession();
        },
      }),
    [clearSession],
  );

  // The API client fires this when the server rejects our token mid-session.
  useEffect(() => {
    setUnauthorizedHandler(clearSession);
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  /**
   * Revalidate the stored token on a cold start rather than trusting a cached
   * user object: the token may have expired (12h, absolute), been revoked by a
   * password reset, or been pruned. Trusting it would render the shell, fire
   * the documents query, take a 401, and bounce the user to sign-in — a visible
   * flash and a wasted round trip. One /auth/me is the cheaper, honest boot.
   */
  const meQuery = useQuery({
    queryKey: authKeys.me(),
    queryFn: () => fetchMe(),
    enabled: token !== null,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    // A failure here means "not signed in", which this provider handles; it is
    // never an error-boundary condition.
    throwOnError: false,
  });

  /**
   * A stored token the server rejects at start-up is dead: drop it so a reload
   * doesn't retry it.
   *
   * Only EXTERNAL state is touched here — no setState. `status` below already
   * derives 'unauthenticated' from the query error, so setting React state
   * would just trigger a second, redundant render pass. Clearing the query
   * cache would be worse still: it would re-enable the query and loop.
   */
  useEffect(() => {
    if (token !== null && meQuery.isError) {
      authStorage.clear();
      setAuthToken(null);
    }
  }, [token, meQuery.isError]);

  /**
   * Everything that turns a token into a signed-in session, in one place —
   * because two endpoints now produce one (`/auth/login` when MFA is off
   * server-side, `/auth/verify-otp` when it is on) and a session established
   * down only one of those paths would be subtly half-built.
   */
  const establishSession = useCallback(
    (result: LoginResponse) => {
      authStorage.set(result.token);
      setAuthToken(result.token);
      setToken(result.token);
      // Seed the cache so the shell renders without a second round trip.
      queryClient.setQueryData<PortalUser>(authKeys.me(), result.user);
    },
    [queryClient],
  );

  const signIn = useCallback(
    async (input: LoginInput): Promise<SignInResult> => {
      try {
        const result = await loginRequest(input);

        // A 200 that is not a token: the password was right and a code is on
        // its way. Nothing is stored — the sign-in isn't finished.
        if (isOtpRequired(result)) {
          return {
            outcome: 'otp-required',
            challenge: result.challenge,
            expiresIn: result.expires_in,
          };
        }

        establishSession(result);

        return { outcome: 'signed-in' };
      } catch (error) {
        const accounts = accountChoicesFrom(error);
        if (accounts) {
          return { outcome: 'account-type-required', accounts };
        }
        throw error;
      }
    },
    [establishSession],
  );

  /**
   * Finishes the sign-in `signIn` started. Rejects with an AppError on a wrong
   * or expired code — the caller decides whether that keeps the user on the
   * code step or sends them back to the beginning.
   */
  const completeOtp = useCallback(
    async (input: VerifyOtpInput): Promise<void> => {
      establishSession(await verifyOtpRequest(input));
    },
    [establishSession],
  );

  const resendOtp = useCallback(
    (challenge: string): Promise<string> => resendOtpRequest(challenge),
    [],
  );

  const signOut = useCallback(async () => {
    const signedOut = authStorage.get();
    try {
      await logoutRequest();
    } catch {
      // A failed revoke must not strand the user in a signed-in shell; the
      // local session is cleared either way and the token expires server-side.
    } finally {
      clearSession();
      if (signedOut) announceSignOut(signedOut);
    }
  }, [clearSession]);

  const status: AuthStatus = useMemo(() => {
    if (token === null) return askingOtherTabs ? 'loading' : 'unauthenticated';
    if (meQuery.isSuccess) return 'authenticated';
    if (meQuery.isError) return 'unauthenticated';
    return 'loading';
  }, [token, askingOtherTabs, meQuery.isSuccess, meQuery.isError]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: status === 'authenticated' ? (meQuery.data ?? null) : null,
      signIn,
      completeOtp,
      resendOtp,
      signOut,
    }),
    [status, meQuery.data, signIn, completeOtp, resendOtp, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
